/**
 * AI Goal Assistant Component
 * Helps youth create SMART goals with AI-powered suggestions
 * Copying ChatGPT's conversational UI pattern
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIGoalAssistant = ({ onGoalCreated, interests = [], skills = [] }) => {
  const { colors } = useTheme();
  const [goalIdea, setGoalIdea] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);

  const getAIHelp = async () => {
    if (!goalIdea.trim()) {
      toast.error('Please describe your goal idea first');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/goal-assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          goal_idea: goalIdea,
          interests,
          skills
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiSuggestion(data);
        toast.success('AI created a SMART goal for you! ✨');
      } else {
        toast.error('AI assistance unavailable');
      }
    } catch (error) {
      console.error('AI error:', error);
      toast.error('Failed to get AI help');
    } finally {
      setLoading(false);
    }
  };

  const useAISuggestion = () => {
    if (aiSuggestion && onGoalCreated) {
      onGoalCreated({
        title: aiSuggestion.smart_goal,
        action_plan: aiSuggestion.action_plan,
        timeline: aiSuggestion.timeline,
        success_metrics: aiSuggestion.success_metrics
      });
      setShowAssistant(false);
      setGoalIdea('');
      setAiSuggestion(null);
    }
  };

  return (
    <div>
      {!showAssistant ? (
        <Button
          onClick={() => setShowAssistant(true)}
          variant="outline"
          className="w-full mb-4"
          style={{ borderColor: '#8B5CF6', color: '#8B5CF6', backgroundColor: 'rgba(139,92,246,0.05)' }}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Get AI Help Setting Your Goal
        </Button>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-6"
        >
          <Card
            className="p-6"
            style={{
              backgroundColor: 'rgba(139,92,246,0.03)',
              borderColor: '#8B5CF6',
              borderWidth: '2px'
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5" style={{ color: '#8B5CF6' }} />
              <h3 className="font-bold" style={{ color: colors.text }}>AI Goal Assistant</h3>
              <Badge className="bg-purple-500/20 text-purple-600 font-semibold">Powered by GPT-4o</Badge>
            </div>

            {!aiSuggestion ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold mb-2 block" style={{ color: colors.text }}>
                    What goal do you want to achieve this summer?
                  </label>
                  <Textarea
                    value={goalIdea}
                    onChange={(e) => setGoalIdea(e.target.value)}
                    placeholder="Example: I want to improve my public speaking skills OR I want to learn Excel for data analysis"
                    rows={3}
                    style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
                  />
                  {interests.length > 0 && (
                    <p className="text-xs mt-2" style={{ color: colors.text_secondary }}>
                      💡 Based on your interests ({interests.join(', ')}), AI will suggest personalized goals
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={getAIHelp}
                    disabled={loading || !goalIdea.trim()}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        AI is thinking...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Get AI Suggestions
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowAssistant(false)}
                    variant="outline"
                    style={{ borderColor: colors.border, color: colors.text }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* AI-Generated SMART Goal */}
                <div className="p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
                  <div className="flex items-start gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold mb-1" style={{ color: colors.text }}>SMART Goal:</p>
                      <p className="font-medium text-base" style={{ color: colors.text }}>
                        {aiSuggestion.smart_goal}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Plan */}
                <div>
                  <p className="text-sm font-semibold mb-2" style={{ color: colors.text }}>Action Plan:</p>
                  <div className="space-y-2">
                    {aiSuggestion.action_plan?.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 mt-0.5" style={{ color: colors.primary }} />
                        <p className="text-sm font-medium" style={{ color: colors.text }}>{step}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timeline & Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.surface }}>
                    <p className="text-xs font-bold mb-1" style={{ color: colors.text }}>Timeline</p>
                    <p className="text-sm font-semibold" style={{ color: colors.text }}>{aiSuggestion.timeline}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: colors.surface }}>
                    <p className="text-xs font-bold mb-1" style={{ color: colors.text }}>Success Metrics</p>
                    <p className="text-xs font-medium" style={{ color: colors.text }}>{aiSuggestion.success_metrics?.[0]}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={useAISuggestion}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Use This Goal
                  </Button>
                  <Button
                    onClick={() => setAiSuggestion(null)}
                    variant="outline"
                    style={{ borderColor: colors.border, color: colors.text }}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default AIGoalAssistant;
